// @ts-check

/**
 * Single source of truth for entity property definitions and utilities.
 * Used by both server-side (EntityService) and client-side components.
 */

// https://github.com/digital-land/extract-alpha/issues/156#issuecomment-3616299570
export const allowedProperties = {
  // https://digital-land.github.io/specification/dataset/tree-preservation-order/
  treepreservationorder: [
    'reference',
    'name',
    'made_date',
    'confirmed_date',
    'entry_date',
    'start_date',
    'end_date',
    'notes',
    'document_url',
    'documentation_url'
  ],
  // https://digital-land.github.io/specification/dataset/tree/
  tree: [
    'reference',
    'name',
    'tree_preservation_order',
    'felled_date',
    'entry_date',
    'start_date',
    'end_date',
    // not sure which of these to include
    'uprn',
    'uprns',
    'address_text',
    'notes'
  ],
  // https://digital-land.github.io/specification/dataset/tree-preservation-zone/
  treepreservationzone: [
    'reference',
    'name',
    'tree_preservation_order',
    'tree_preservation_zone_type',
    'entry_date',
    'start_date',
    'end_date',
    'notes'
  ],
  // https://digital-land.github.io/specification/dataset/conservation-area-document/
  conservationareadocument: [
    'reference',
    'name',
    'document_type',
    'entry_date',
    'start_date',
    'end_date',
    'notes'
  ],
  // https://digital-land.github.io/specification/dataset/conservation-area/
  conservationarea: [
    'reference',
    'name',
    'designation_date',
    'entry_date',
    'start_date',
    'end_date',
    'organisation',
    'notes',
    'document_url',
    'documentation_url'
  ],
  // https://digital-land.github.io/specification/dataset/article-4-direction/
  article4direction: [
    'reference',
    'name',
    'start_date',
    'end_date',
    'entry_date',
    'notes',
    'description',
    'document_url',
    'documentation_url'
  ],
  // https://digital-land.github.io/specification/dataset/article-4-direction-area/
  article4directionarea: [
    'reference',
    'name',
    'start_date',
    'end_date',
    'entry_date',
    'article4direction',
    'permitted_development_rights',
    'permitted_development_rights_details',
    'uprns',
    'address_text',
    'notes'
  ]
}

/**
 * Get only allowed properties for an entity based on its type
 * @param {*} entity
 * @param {string} entityType
 * @returns {Record<string, any>}
 */
export function getAllowedEntityPropertiesForEntity (entity, entityType) {
  const allowed = allowedProperties[entityType]
  const props = entity?.properties || {}

  if (!entityType || !Array.isArray(allowed)) {
    // Return all properties if entityType is invalid
    return props
  }

  // Only include keys that exist in props, in allowed order
  return Object.fromEntries(
    allowed
      .filter((key) => Object.prototype.hasOwnProperty.call(props, key))
      .map((key) => [key, props[key]])
  )
}

/**
 * Determine the entity type based on job type and entity properties
 * @param {string} jobType
 * @param {*} entity
 * @param {boolean} entityIsMetadata
 * @returns {string}
 */
export function getEntityType (jobType, entity, entityIsMetadata) {
  let entityType = jobType

  // If metadata, return job type directly since it will match
  if (entityIsMetadata) {
    return entityType
  }

  switch (jobType) {
    case 'article4direction':
      entityType = 'article4directionarea'
      break
    case 'treepreservationorder': {
      // Use feature_type property as primary source of truth
      const featureType = entity?.properties?.feature_type
      if (featureType === 'tree') {
        entityType = 'tree'
      } else if (featureType === 'tree_preservation_zone') {
        entityType = 'treepreservationzone'
      } else if (entity?.geometry?.type) {
        // Fallback to geometry type for legacy data without feature_type
        if (entity.geometry.type === 'Point') {
          entityType = 'tree'
        } else if (entity.geometry.type === 'Polygon' || entity.geometry.type === 'MultiPolygon') {
          entityType = 'treepreservationzone'
        }
      }
      // Otherwise keep default 'treepreservationorder'
      break
    }
    default:
      // keep original entityType
      break
  }

  return entityType
}
